/**
 * @module modules/calendar-sk
 * @description <h2><code>calendar-sk</code></h2>
 *
 * Displays an accessible calendar, one month at a time, and allows selecting a
 * single day. Offers the ability to navigate by both month and year. Also is
 * themeable and offers keyboard navigation.
 *
 * Why not use input type="date"? It doesn't work on Safari, and the pop-up
 * calendar isn't styleable.
 *
 * Why not use the Elix web component? It is not themeable (at least not
 * easily), and it is also inaccessible.
 *
 * Accessibility advice was derived from this page:
 *   https://www.w3.org/TR/wai-aria-practices/examples/dialog-modal/datepicker-dialog.html
 *
 * @evt change - A CustomEvent with the selected Date in the detail.
 *
 * This element provides a keyboardHandler callback that should be attached and
 * detached to/from the appropriate containing element when it is used, for
 * example, a containing 'dialog' element.
 */ "use strict";
Object.defineProperty(exports, "__esModule", {
    value: true
});
Object.defineProperty(exports, "CalendarSk", {
    enumerable: true,
    get: function() {
        return CalendarSk;
    }
});
const _lit = require("lit");
const _decorators = require("lit/decorators.js");
require("../../../elements-sk/modules/icons/navigate-before-icon-sk");
require("../../../elements-sk/modules/icons/navigate-next-icon-sk");
function _ts_decorate(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
/*
 * Most of the Date[1] object's methods return zero-indexed values, with exceptions such as
 * Date.prototype.getDate() and Date.prototype.getYear().
 *
 * To make things easier to follow, we suffix zero-indexed values returned by a
 * Date object with "Index", e.g.:
 *
 *   const dayIndex = date.getDay();     // (0-6).
 *   const monthIndex = date.getMonth(); // (0-11).
 *
 * [1] https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date
 */ /**
 * Jump forward one month, and back one day to get the last day of the month.
 * Since days are 1-indexed, a value of 0 represents the last day of the
 * previous month.
 */ const getNumberOfDaysInMonth = (year, monthIndex)=>new Date(year, monthIndex + 1, 0).getDate();
// Returns a day of the week [0-6]
const firstDayIndexOfMonth = (year, monthIndex)=>new Date(year, monthIndex).getDay();
// Used in templates.
const sevenDaysInAWeek = [
    0,
    1,
    2,
    3,
    4,
    5,
    6
];
// To display a month we need to display up to 6 weeks. Used in templates.
const sixWeeks = [
    0,
    1,
    2,
    3,
    4,
    5
];
// The dates that CalendarSk manipulates, always in local time.
let CalendarDate = class CalendarDate {
    equal(d) {
        return this.year === d.year && this.monthIndex === d.monthIndex && this.date === d.date;
    }
    constructor(d){
        this.year = d.getFullYear();
        this.monthIndex = d.getMonth();
        this.date = d.getDate();
    }
};
let CalendarSk = class CalendarSk extends _lit.LitElement {
    createRenderRoot() {
        return this;
    }
    render() {
        return (0, _lit.html)`
      <table class="calendar">
        <tr>
          <th>
            <button
              @click=${this.decYear}
              aria-label="Previous year"
              title="Previous year"
              id="previous-year">
              <navigate-before-icon-sk></navigate-before-icon-sk>
            </button>
          </th>
          <th colspan="5">
            <h2 aria-live="polite" id="calendar-year">
              ${new Intl.DateTimeFormat(this.locale, {
            year: 'numeric'
        }).format(this._displayDate)}
            </h2>
          </th>
          <th>
            <button @click=${this.incYear} aria-label="Next year" title="Next year" id="next-year">
              <navigate-next-icon-sk></navigate-next-icon-sk>
            </button>
          </th>
        </tr>
        <tr>
          <th>
            <button
              @click=${this.decMonth}
              aria-label="Previous month"
              title="Previous month"
              id="previous-month">
              <navigate-before-icon-sk></navigate-before-icon-sk>
            </button>
          </th>
          <th colspan="5">
            <h2 aria-live="polite" id="calendar-month">
              ${new Intl.DateTimeFormat(this.locale, {
            month: 'long'
        }).format(this._displayDate)}
            </h2>
          </th>
          <th>
            <button
              @click=${this.incMonth}
              aria-label="Next month"
              title="Next month"
              id="next-month">
              <navigate-next-icon-sk></navigate-next-icon-sk>
            </button>
          </th>
        </tr>
        ${this.weekDayHeaderTemplate()} ${sixWeeks.map((i)=>this.rowTemplate(i))}
      </table>
    `;
    }
    buttonForDateTemplate(date, daysInMonth, selected) {
        if (date < 1 || date > daysInMonth) {
            return (0, _lit.html)``;
        }
        return (0, _lit.html)`
      <button
        class="action"
        @click=${this.dateClick}
        data-date=${date}
        tabindex=${selected ? 0 : -1}
        aria-selected=${selected}>
        ${date}
      </button>
    `;
    }
    rowTemplate(weekIndex) {
        const year = this.displayYear();
        const monthIndex = this.displayMonthIndex();
        const today = new CalendarDate(new Date());
        // If June starts on a Tuesday then IndexOfTheFirstDayOfTheMonth = 2,
        // which means if we are filling in the first row we want to leave the first
        // two days (Sunday and Monday) blank.
        const firstDayOfTheMonthIndex = firstDayIndexOfMonth(year, monthIndex);
        const daysInMonth = getNumberOfDaysInMonth(year, monthIndex);
        const selectedDate = this._displayDate.getDate();
        const currentDate = new CalendarDate(this._displayDate);
        return (0, _lit.html)`
      <tr>
        ${sevenDaysInAWeek.map((i)=>{
            const date = 7 * weekIndex + i + 1 - firstDayOfTheMonthIndex;
            currentDate.date = date;
            const selected = selectedDate === date;
            return (0, _lit.html)`
            <td
              class="
            ${currentDate.equal(today) ? 'today' : ''}
            ${selected ? 'selected' : ''}
          ">
              ${this.buttonForDateTemplate(date, daysInMonth, selected)}
            </td>
          `;
        })}
      </tr>
    `;
    }
    weekDayHeaderTemplate() {
        const narrowFormatter = new Intl.DateTimeFormat(this.locale, {
            weekday: 'narrow'
        });
        const longFormatter = new Intl.DateTimeFormat(this.locale, {
            weekday: 'long'
        });
        return (0, _lit.html)`
      <tr class="weekdayHeader">
        ${sevenDaysInAWeek.map((i)=>(0, _lit.html)`
            <td>
              <span abbr="${longFormatter.format(new Date(2020, 2, i + 1))}">
                ${narrowFormatter.format(new Date(2020, 2, i + 1))}
              </span>
            </td>
          `)}
      </tr>
    `;
    }
    /**
   * Attach this handler to the 'keydown' event on the appropriate elements
   * parent, such as document or a 'dialog' element.
   *
   * Allows finer grained control of keyboard events on a page with more
   * than one keyboard listener.
   */ keyboardHandler(e) {
        let keyHandled = true;
        switch(e.code){
            case 'PageUp':
                this.decMonth();
                break;
            case 'PageDown':
                this.incMonth();
                break;
            case 'ArrowRight':
                this.incDay();
                break;
            case 'ArrowLeft':
                this.decDay();
                break;
            case 'ArrowUp':
                this.decWeek();
                break;
            case 'ArrowDown':
                this.incWeek();
                break;
            default:
                keyHandled = false;
                break;
        }
        if (keyHandled) {
            e.stopPropagation();
            e.preventDefault();
            this.querySelector('button[aria-selected="true"]').focus();
        }
    }
    dateClick(e) {
        const d = new Date(this._displayDate);
        d.setDate(+e.target.dataset.date);
        this.dispatchEvent(new CustomEvent('change', {
            detail: d,
            bubbles: true
        }));
        this._displayDate = d;
    }
    displayYear() {
        return this._displayDate.getFullYear();
    }
    displayMonthIndex() {
        return this._displayDate.getMonth();
    }
    incYear() {
        const year = this.displayYear();
        const monthIndex = this.displayMonthIndex();
        let date = this._displayDate.getDate();
        const daysInMonth = getNumberOfDaysInMonth(year + 1, monthIndex);
        if (date > daysInMonth) {
            date = daysInMonth;
        }
        this._displayDate = new Date(year + 1, monthIndex, date);
    }
    decYear() {
        const year = this.displayYear();
        const monthIndex = this.displayMonthIndex();
        let date = this._displayDate.getDate();
        const daysInMonth = getNumberOfDaysInMonth(year - 1, monthIndex);
        if (date > daysInMonth) {
            date = daysInMonth;
        }
        this._displayDate = new Date(year - 1, monthIndex, date);
    }
    incMonth() {
        let year = this.displayYear();
        let monthIndex = this.displayMonthIndex();
        let date = this._displayDate.getDate();
        monthIndex += 1;
        if (monthIndex > 11) {
            monthIndex = 0;
            year += 1;
        }
        const daysInMonth = getNumberOfDaysInMonth(year, monthIndex);
        if (date > daysInMonth) {
            date = daysInMonth;
        }
        this._displayDate = new Date(year, monthIndex, date);
    }
    decMonth() {
        let year = this.displayYear();
        let monthIndex = this.displayMonthIndex();
        let date = this._displayDate.getDate();
        monthIndex -= 1;
        if (monthIndex < 0) {
            monthIndex = 11;
            year -= 1;
        }
        const daysInMonth = getNumberOfDaysInMonth(year, monthIndex);
        if (date > daysInMonth) {
            date = daysInMonth;
        }
        this._displayDate = new Date(year, monthIndex, date);
    }
    incDay() {
        const year = this.displayYear();
        const monthIndex = this.displayMonthIndex();
        const date = this._displayDate.getDate();
        this._displayDate = new Date(year, monthIndex, date + 1);
    }
    decDay() {
        const year = this.displayYear();
        const monthIndex = this.displayMonthIndex();
        const date = this._displayDate.getDate();
        this._displayDate = new Date(year, monthIndex, date - 1);
    }
    incWeek() {
        const year = this.displayYear();
        const monthIndex = this.displayMonthIndex();
        const date = this._displayDate.getDate();
        this._displayDate = new Date(year, monthIndex, date + 7);
    }
    decWeek() {
        const year = this.displayYear();
        const monthIndex = this.displayMonthIndex();
        const date = this._displayDate.getDate();
        this._displayDate = new Date(year, monthIndex, date - 7);
    }
    /** The date to display on the calendar. */ get displayDate() {
        return this._displayDate;
    }
    set displayDate(v) {
        this._displayDate = v;
    }
    constructor(...args){
        super(...args);
        this._displayDate = new Date();
        this.locale = undefined;
    }
};
_ts_decorate([
    (0, _decorators.state)(),
    _ts_metadata("design:type", typeof Date === "undefined" ? Object : Date)
], CalendarSk.prototype, "_displayDate", void 0);
_ts_decorate([
    (0, _decorators.property)(),
    _ts_metadata("design:type", Object)
], CalendarSk.prototype, "locale", void 0);
CalendarSk = _ts_decorate([
    (0, _decorators.customElement)('calendar-sk')
], CalendarSk);
